// SettingsPage.js
import { useEffect, useState } from 'react';
import { api } from '../lib/auth';
import '../farmDashboard.css';
import { stagger } from '../lib/stagger';
import { Button, Input, Select, Field, Checkbox, PageHeader } from '../lib/ui';

// Accepted roles that unlock server editing
const ADMIN_ROLES = ['admin', 'administrator', 'owner', 'superadmin'];

/* ---------- Small helpers ---------- */
const Section = ({ title, subtitle, children, right, style }) => (
  <section className="chart-container card" style={{ padding: 16, ...(style || {}) }}>
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        marginBottom: 8, ...stagger(0, 'td-fade-down', 20, 60, 380)
      }}
    >
      <div>
        <h3 style={{ margin: '0 0 6px' }}>{title}</h3>
        {subtitle && <small style={{ color: 'var(--muted)' }}>{subtitle}</small>}
      </div>
      {right}
    </div>
    <div style={{ display: 'grid', gap: 10 }}>{children}</div>
  </section>
);

/* ---------- Advanced raw editor (standalone tab) ---------- */
function AdvancedJsonCard() {

  const [isAdmin, setIsAdmin] = useState(false);
  const [text, setText] = useState('{}');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [newDemandName, setNewDemandName] = useState('');
const [addingDemand, setAddingDemand] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/auth/me');
        setIsAdmin(ADMIN_ROLES.includes((me?.data?.user?.role || '').toLowerCase()));
        const cfg = await api.get('/config');
        setText(JSON.stringify(cfg?.data?.config ?? {}, null, 2));
      } catch (e) {
        setErr(e?.response?.data?.error || 'Unable to load config.');
      }
    })();
  }, []);

  const put = async () => {
    setErr(''); setMsg('');
    try {
      const obj = JSON.parse(text);
      await api.put('/config', obj);
      setMsg('Saved (PUT).');
    } catch (e) {
      setErr(e?.response?.data?.error || 'Save failed.');
    }
  };

  // ---------- helpers so the quick editors can manipulate JSON ----------
  const cfg = (() => { try { return JSON.parse(text || '{}'); } catch { return {}; } })();
  const write = (next) => setText(JSON.stringify(next, null, 2));
  const clone = (o) => JSON.parse(JSON.stringify(o ?? {}));
  const setDot = (obj, path, value) => {
    const keys = Array.isArray(path) ? path : String(path).split('.');
    let o = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (typeof o[k] !== 'object' || o[k] === null) o[k] = {};
      o = o[k];
    }
    o[keys[keys.length - 1]] = value;
    return obj;
  };
  const update = (path, value) => {
    const next = clone(cfg);
    write(setDot(next, path, value));
  };

  // AC helpers
  const acRows = Array.isArray(cfg?.ac?.schedule) ? cfg.ac.schedule : [];
  const updateAC = (i, patch) => {
    const next = clone(cfg);
    next.ac = next.ac || {};
    next.ac.schedule = (next.ac.schedule || []).map((r, idx) => idx === i ? { ...r, ...patch } : r);
    write(next);
  };
  const addAC = () => {
    const next = clone(cfg);
    next.ac = next.ac || {};
    next.ac.schedule = [...(next.ac.schedule || []), { afterHour: 16, power: true, mode: 'cold', temp: 24, fan: 'low' }];
    write(next);
  };
  const removeAC = (i) => {
    const next = clone(cfg);
    next.ac = next.ac || {};
    next.ac.schedule = (next.ac.schedule || []).filter((_, idx) => idx !== i);
    write(next);
  };

  // Flooding helpers
  const durations = cfg?.flooding?.durationsMs || {};
  const setDuration = (layerKey, val) => {
    const next = clone(cfg);
    next.flooding = next.flooding || {};
    next.flooding.durationsMs = { ...(next.flooding.durationsMs || {}), [layerKey]: Number(val) || 0 };
    write(next);
  };
  const layerBySlot = Array.isArray(cfg?.flooding?.layerBySlot) ? cfg.flooding.layerBySlot : [];
  const setLayerBySlotCSV = (csv) => {
    const parsed = String(csv).split(',').map(s => {
      const t = s.trim().toLowerCase();
      if (t === 'null' || t === '-') return null;
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    });
    const next = clone(cfg);
    next.flooding = next.flooding || {};
    next.flooding.layerBySlot = parsed;
    write(next);
  };

  // DEMAND helpers (new)
  const demandObj = cfg?.tasksConfig?.DEMAND || {};
  const sortedDemand = Object.entries(demandObj).sort(([a],[b]) => a.localeCompare(b));

  const ensureTasksConfig = (next) => {
    next.tasksConfig = next.tasksConfig || {};
    next.tasksConfig.DEMAND = next.tasksConfig.DEMAND || {};
  };

const addDemandCrop = () => {
  const trimmed = newDemandName.trim();
  if (!trimmed) return;

  const next = clone(cfg);
  ensureTasksConfig(next);

  if (next.tasksConfig.DEMAND[trimmed]) {
    setErr('Crop already exists.');
    return;
  }

  next.tasksConfig.DEMAND[trimmed] = {
    needsTransplant: false,
    expansionFactor: 1,
    targets: { starter: 0 },
    transplantAfterDays: next.tasksConfig.defaultTransplantDays ?? 14
  };

  write(next);
  setNewDemandName('');
  setAddingDemand(false);
};

  const removeDemandCrop = (name) => {
    if (!window.confirm(`Delete "${name}" from DEMAND?`)) return;
    const next = clone(cfg);
    ensureTasksConfig(next);
    delete next.tasksConfig.DEMAND[name];
    write(next);
  };

  const renameDemandCrop = (oldName, newName) => {
    const newTrim = (newName || '').trim();
    if (!newTrim || newTrim === oldName) return;
    const next = clone(cfg);
    ensureTasksConfig(next);
    if (next.tasksConfig.DEMAND[oldName]) {
      next.tasksConfig.DEMAND[newTrim] = next.tasksConfig.DEMAND[oldName];
      delete next.tasksConfig.DEMAND[oldName];
      write(next);
    }
  };

  const patchDemand = (name, patch) => {
    const next = clone(cfg);
    ensureTasksConfig(next);
    const cur = next.tasksConfig.DEMAND[name] || {};
    next.tasksConfig.DEMAND[name] = { ...cur, ...patch };
    write(next);
  };

  const patchDemandTargets = (name, key, value) => {
    const next = clone(cfg);
    ensureTasksConfig(next);
    const cur = next.tasksConfig.DEMAND[name] || {};
    const t = { ...(cur.targets || {}) };
    t[key] = value;
    next.tasksConfig.DEMAND[name] = { ...cur, targets: t };
    write(next);
  };

  // Search/filter state for DEMAND UI
  const [dq, setDQ] = useState('');
  const filteredDemand = sortedDemand.filter(([name]) =>
    !dq || name.toLowerCase().includes(dq.toLowerCase())
  );

  return (
    <Section
      title="Advanced JSON"
      subtitle="Quick editor for common settings (writes into the JSON below). Use Save (PUT) to persist."
      right={<div style={{ display:'flex', gap:8 }}>
        <Button variant="primary" onClick={put} disabled={!isAdmin} style={stagger(0)}>Save (PUT)</Button>
      </div>}
      style={stagger(0)}
    >
      {err && (
        <div style={{ marginBottom:8, padding:10, background:'#fff5f5', border:'1px solid #f0caca', borderRadius:'var(--r-lg)', color:'#b00020', ...stagger(1) }}>
          {err}
        </div>
      )}
      {msg && (
        <div style={{ marginBottom:8, padding:10, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'var(--r-lg)', color:'#15803d', ...stagger(2) }}>
          {msg}
        </div>
      )}

      {/* ---- QUICK EDITOR ---- */}
      <div className="grid-12" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        {/* Mongo */}
        <Section title="MongoDB" style={stagger(0)}>
          <Field label="URI" style={stagger(0)}>
            <Input
              value={cfg?.mongo?.uri || ''}
              onChange={(e)=>update('mongo.uri', e.target.value)}
              placeholder="mongodb://localhost:27017/garageFarm"
            />
          </Field>
          <Field label="serverSelectionTimeoutMS" style={stagger(1)}>
            <Input
              type="number"
              value={cfg?.mongo?.options?.serverSelectionTimeoutMS ?? 5000}
              onChange={(e)=>update('mongo.options.serverSelectionTimeoutMS', parseInt(e.target.value||'0',10))}
            />
          </Field>
        </Section>

        {/* Scheduler / TZ */}
        <Section title="Scheduler & Timezone" style={stagger(1)}>
          <Field label="commandSetterIntervalMinutes" style={stagger(0)}>
            <Input
              type="number"
              value={cfg?.scheduler?.commandSetterIntervalMinutes ?? 20}
              onChange={(e)=>update('scheduler.commandSetterIntervalMinutes', parseInt(e.target.value||'0',10))}
            />
          </Field>
          <Field label="dailyTasksCron" style={stagger(1)}>
            <Input
              value={cfg?.scheduler?.dailyTasksCron || '0 6 * * *'}
              onChange={(e)=>update('scheduler.dailyTasksCron', e.target.value)}
            />
          </Field>
          <Field label="timezone" style={stagger(2)}>
            <Input
              value={cfg?.timezone || 'Europe/London'}
              onChange={(e)=>update('timezone', e.target.value)}
            />
          </Field>
        </Section>

        {/* Lights */}
        <Section title="Lights" style={stagger(2)}>
          <Field label="startHour" style={stagger(0)}>
            <Input
              type="number"
              value={cfg?.lights?.startHour ?? 0}
              onChange={(e)=>update('lights.startHour', parseInt(e.target.value||'0',10))}
            />
          </Field>
          <Field label="endHour" style={stagger(1)}>
            <Input
              type="number"
              value={cfg?.lights?.endHour ?? 16}
              onChange={(e)=>update('lights.endHour', parseInt(e.target.value||'0',10))}
            />
          </Field>
        </Section>

        {/* Flooding */}
        <Section title="Flooding" style={stagger(3)}>
          <Field label="slotMinutes" style={stagger(0)}>
            <Input
              type="number"
              value={cfg?.flooding?.slotMinutes ?? 20}
              onChange={(e)=>update('flooding.slotMinutes', parseInt(e.target.value||'0',10))}
            />
          </Field>
          <div style={{ marginTop:10, ...stagger(1) }}>
            <div style={{ fontWeight:600, marginBottom:6 }}>Layer durations (ms)</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7, minmax(80px,1fr))', gap:8 }}>
              {['1','2','3','4','5','6','7'].map((k, i)=>(
                <div key={k} style={{ display:'grid', gap:6, ...stagger(i, 'td-fade-up', 40, 50, 360) }}>
                  <label style={{ fontSize:12, color:'var(--muted)' }}>Layer {k}</label>
                  <Input
                    type="number"
                    value={durations?.[k] ?? 0}
                    onChange={(e)=>setDuration(k, parseInt(e.target.value||'0',10))}
                  />
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop:12, ...stagger(2) }}>
            <Field label="layerBySlot (CSV)">
              <Input
                value={layerBySlot.map(v => (v==null ? 'null' : String(v))).join(',')}
                onChange={(e)=>setLayerBySlotCSV(e.target.value)}
                placeholder="7,6,5,4,3,2,1,null,null"
              />
            </Field>
          </div>
        </Section>
      </div>

      {/* AC schedule editor */}
      <Section title="AC Schedule" subtitle="Rules applied by hour. The last rule whose hour has passed wins." style={stagger(1)}>
        <div style={{
          display:'grid',
          gridTemplateColumns:'90px 90px 120px 120px 120px 90px',
          gap:10, fontWeight:600, color:'var(--muted)', marginBottom:6, fontSize:11, textTransform:'uppercase', letterSpacing:'.04em',
          ...stagger(0, 'td-fade-down', 40, 60, 380)
        }}>
          <div>After hour</div><div>Power</div><div>Mode</div><div>Temp (°C)</div><div>Fan</div><div>Actions</div>
        </div>
        <div style={{ display:'grid', gap:8 }}>
          {acRows.map((row, i) => (
            <div key={i} style={{
              background:'var(--panel)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:10,
              display:'grid', gridTemplateColumns:'90px 90px 120px 120px 120px 90px', gap:10, alignItems:'center',
              ...stagger(i, 'td-fade-up', 40, 70, 420)
            }}>
              <Input type="number" value={row.afterHour ?? 0}
                     onChange={(e)=>updateAC(i, { afterHour: parseInt(e.target.value||'0',10) })}/>
              <div>
                <Checkbox checked={!!row.power}
                          onChange={(e)=>updateAC(i, { power: e.target.checked })}
                          label="On"/>
              </div>
              <Select value={row.mode || 'cold'} onChange={(e)=>updateAC(i, { mode: e.target.value })}>
                <option value="cold">cold</option><option value="heat">heat</option>
                <option value="dry">dry</option><option value="fan">fan</option><option value="auto">auto</option>
              </Select>
              <Input type="number" value={row.temp ?? 24}
                     onChange={(e)=>updateAC(i, { temp: parseInt(e.target.value||'0',10) })}/>
              <Select value={row.fan || 'low'} onChange={(e)=>updateAC(i, { fan: e.target.value })}>
                <option value="low">low</option><option value="med">med</option>
                <option value="high">high</option><option value="auto">auto</option>
              </Select>
              <div><Button variant="danger" onClick={()=>removeAC(i)}>Remove</Button></div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:10 }}>
          <Button onClick={addAC} style={stagger(0)}>+ Add rule</Button>
        </div>
      </Section>

      {/* NEW: DEMAND Editor */}


      {/* Raw JSON textarea (still editable) */}
      <Section title="Raw JSON" subtitle="Edits above write into this. You can also tweak directly here before saving." style={stagger(3)}>
        <textarea
          rows={18}
          value={text}
          onChange={(e)=>setText(e.target.value)}
          disabled={!isAdmin}
          style={{
            width:'100%', fontFamily:'ui-monospace, Menlo, Consolas, monospace', fontSize:13,
            padding:12, border:'1px solid var(--border)', borderRadius:'var(--r-lg)',
            background: isAdmin ? 'var(--panel)' : 'var(--bg)',
            color: 'var(--text)', outline: 'none',
            ...stagger(0)
          }}
        />
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8, ...stagger(1) }}>
          <Button variant="primary" onClick={put} disabled={!isAdmin}>Save (PUT)</Button>
        </div>
      </Section>
    </Section>
  );
}


/* ---------- Master page ---------- */
export default function SettingsPage() {
  return (
    <div className="dashboard-container">
      <PageHeader title="Settings" subtitle="Server configuration for the farm" />

      <div style={{ display: 'grid', gap: 16 }}>
        <AdvancedJsonCard />
      </div>
    </div>
  );
}
